/**
 * Reglas de negocio puras para Contratos Marco.
 */

import type { ContratoMarco, Rd, So, Tarifa } from "@app-types/entities";
import type { EstadoContrato } from "@constants/estados";

const TRANSICIONES_CONTRATO: Record<EstadoContrato, EstadoContrato[]> = {
  BORRADOR: ["EN_APROBACION"],
  EN_APROBACION: ["VIGENTE", "BORRADOR"],
  VIGENTE: ["VENCIDO", "TERMINADO"],
  VENCIDO: ["VIGENTE", "TERMINADO"], // por extension de vigencia
  TERMINADO: [],
};

export function puedeTransicionarContrato(
  actual: EstadoContrato,
  destino: EstadoContrato,
): boolean {
  return TRANSICIONES_CONTRATO[actual]?.includes(destino) ?? false;
}

/** Verifica si el contrato esta vigente HOY (estado y fechas). */
export function estaVigenteHoy(contrato: ContratoMarco, hoy = new Date()): boolean {
  if (contrato.estado !== "VIGENTE") return false;
  const hoyIso = hoy.toISOString().slice(0, 10);
  return hoyIso >= contrato.vigenciaInicio && hoyIso <= contrato.vigenciaFin;
}

/** Dias restantes de vigencia. Negativo si ya vencio. */
export function diasRestantes(contrato: ContratoMarco, hoy = new Date()): number {
  const fin = new Date(contrato.vigenciaFin);
  const ms = fin.getTime() - hoy.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

/** Detecta contratos por vencer (proximos N dias). */
export function porVencer(contratos: ContratoMarco[], dias = 30): ContratoMarco[] {
  return contratos.filter((c) => {
    if (c.estado !== "VIGENTE") return false;
    const r = diasRestantes(c);
    return r > 0 && r <= dias;
  });
}

/** Validacion del contrato. */
export function validarContrato(
  c: Pick<ContratoMarco, "codigo" | "contratistaNombre" | "vigenciaInicio" | "vigenciaFin" | "montoTecho">,
): { valido: boolean; errores: string[] } {
  const errores: string[] = [];
  if (!c.codigo?.trim()) errores.push("Codigo es obligatorio");
  if (!c.contratistaNombre?.trim()) errores.push("Contratista es obligatorio");
  if (!c.vigenciaInicio) errores.push("Fecha inicio de vigencia obligatoria");
  if (!c.vigenciaFin) errores.push("Fecha fin de vigencia obligatoria");
  if (c.vigenciaInicio && c.vigenciaFin && c.vigenciaInicio > c.vigenciaFin) {
    errores.push("Fecha inicio debe ser anterior a fecha fin");
  }
  if (!c.montoTecho || c.montoTecho <= 0) errores.push("Monto techo debe ser mayor a cero");
  return { valido: errores.length === 0, errores };
}

/** Validacion de una tarifa individual. */
export function validarTarifa(t: Pick<Tarifa, "codigo" | "descripcion" | "unidad" | "precio">): {
  valido: boolean;
  errores: string[];
} {
  const errores: string[] = [];
  if (!t.codigo?.trim()) errores.push("Codigo de tarifa obligatorio");
  if (!t.descripcion?.trim()) errores.push("Descripcion obligatoria");
  if (!t.unidad?.trim()) errores.push("Unidad obligatoria");
  if (t.precio <= 0) errores.push("Precio debe ser mayor a cero");
  return { valido: errores.length === 0, errores };
}

/** Detecta tarifas duplicadas (mismo codigo). */
export function tarifasDuplicadas(tarifas: Tarifa[]): string[] {
  const counts: Record<string, number> = {};
  for (const t of tarifas) counts[t.codigo] = (counts[t.codigo] ?? 0) + 1;
  return Object.entries(counts)
    .filter(([, n]) => n > 1)
    .map(([cod]) => cod);
}

// ============================================================================
// Contabilidad de consumo por SOs
// ============================================================================
//
// Un Contrato Marco es un acuerdo de tarifas vigente durante un periodo largo.
// Las SOs que se emiten bajo él **NO** lo bloquean por monto, pero sí dejan
// huella: cada SO emitida descuenta del techo del CM como métrica contable.
// El módulo de Contratos Marco usa estos helpers para mostrar el consumo
// histórico por contrato y agregado por moneda.

/** Resumen de consumo de un Contrato Marco por SOs emitidas. */
export interface ConsumoContrato {
  contratoId: string;
  /** Monto total emitido (suma de SOs no canceladas, excluyendo adicionales). */
  consumido: number;
  /** Margen restante = max(0, montoTecho - consumido). Métrica informativa. */
  margen: number;
  /** Porcentaje consumido respecto al techo (0-100; puede pasar 100). */
  porcentaje: number;
  /** Número de SOs no canceladas (excluye adicionales). */
  numSos: number;
  /** Desglose por estado de SO. */
  porEstado: Record<string, { numero: number; monto: number }>;
  /** SOs adicionales asociadas (no descuentan, pero se cuentan aparte). */
  numAdicionales: number;
  /** Monto emitido en SOs adicionales (no entra en `consumido`). */
  montoAdicionales: number;
}

/**
 * Calcula el consumo de un Contrato Marco a partir de las SOs emitidas.
 *
 * Reglas:
 *   - Sumamos SOs con `contratoMarcoId === contrato.id` y `estado !== "CANCELADA"`.
 *   - Las SOs adicionales (esAdicional=true) NO entran en `consumido` —
 *     se reportan en `numAdicionales` / `montoAdicionales` para visibilidad.
 *   - El "margen" es informativo: el contrato no bloquea por superar el techo.
 */
/** Coerciona un monto a número seguro. Defiende contra strings o NaN llegados
 *  desde el storage. */
function safeMonto(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function consumoContrato(
  contrato: Pick<ContratoMarco, "id" | "montoTecho" | "valorYaConsumido">,
  sos: So[],
): ConsumoContrato {
  const propias = sos.filter(
    (s) => s.contratoMarcoId === contrato.id && s.estado !== "CANCELADA",
  );
  const principales = propias.filter((s) => !s.esAdicional);
  const adicionales = propias.filter((s) => s.esAdicional);

  // Consumo previo (POs/SOs anteriores al PMIS) + SOs principales del PMIS.
  const consumidoPrevio = safeMonto(contrato.valorYaConsumido);
  const consumido =
    consumidoPrevio + principales.reduce((acc, s) => acc + safeMonto(s.monto), 0);
  const montoAdicionales = adicionales.reduce((acc, s) => acc + safeMonto(s.monto), 0);
  const techo = safeMonto(contrato.montoTecho);
  const margen = Math.max(0, techo - consumido);
  const porcentaje = techo > 0 ? (consumido / techo) * 100 : 0;

  const porEstado: Record<string, { numero: number; monto: number }> = {};
  for (const s of principales) {
    const entry = porEstado[s.estado] ?? { numero: 0, monto: 0 };
    entry.numero += 1;
    entry.monto += safeMonto(s.monto);
    porEstado[s.estado] = entry;
  }

  return {
    contratoId: contrato.id,
    consumido,
    margen,
    porcentaje,
    numSos: principales.length,
    porEstado,
    numAdicionales: adicionales.length,
    montoAdicionales,
  };
}

/**
 * Devuelve un Map<contratoId, ConsumoContrato> con el consumo de cada contrato.
 * Optimizado para listados grandes: recorre las SOs una sola vez.
 */
export function consumoPorContrato(
  contratos: Pick<ContratoMarco, "id" | "montoTecho">[],
  sos: So[],
): Map<string, ConsumoContrato> {
  const idsCM = new Set(contratos.map((c) => c.id));
  const sosPorCM = new Map<string, So[]>();
  for (const s of sos) {
    if (!s.contratoMarcoId || !idsCM.has(s.contratoMarcoId)) continue;
    const arr = sosPorCM.get(s.contratoMarcoId) ?? [];
    arr.push(s);
    sosPorCM.set(s.contratoMarcoId, arr);
  }
  const result = new Map<string, ConsumoContrato>();
  for (const c of contratos) {
    result.set(c.id, consumoContrato(c, sosPorCM.get(c.id) ?? []));
  }
  return result;
}

/** KPIs para mostrar arriba del listado. */
export function kpisContratos(
  contratos: ContratoMarco[],
  sos: So[] = [],
): {
  total: number;
  vigentes: number;
  porVencerProx30: number;
  /** Monto techo agregado POR MONEDA (no se suman monedas distintas). */
  montoTechoPorMoneda: Record<string, number>;
  /** Consumo agregado POR MONEDA sobre los contratos vigentes. */
  consumidoPorMoneda: Record<string, number>;
} {
  const vigentes = contratos.filter((c) => c.estado === "VIGENTE");
  const montoTechoPorMoneda: Record<string, number> = {};
  const consumidoPorMoneda: Record<string, number> = {};
  const consumos = consumoPorContrato(vigentes, sos);
  for (const c of vigentes) {
    montoTechoPorMoneda[c.moneda] = (montoTechoPorMoneda[c.moneda] ?? 0) + c.montoTecho;
    const consumo = consumos.get(c.id);
    if (consumo) {
      consumidoPorMoneda[c.moneda] =
        (consumidoPorMoneda[c.moneda] ?? 0) + consumo.consumido;
    }
  }
  return {
    total: contratos.length,
    vigentes: vigentes.length,
    porVencerProx30: porVencer(contratos, 30).length,
    montoTechoPorMoneda,
    consumidoPorMoneda,
  };
}

// ============================================================================
// Desempeño del contratista — calificación media de los RDs (VD §6.7.4)
// ============================================================================
//
// Cada RD lleva una calificación 1-5 ⭐ propuesta por el Interventor al
// aprobarlo (`Rd.calificacionInt`). El PL2 y el PL ven esa calificación
// cuando revisan el RD antes de integrarlo al PMIS. La media de las
// calificaciones por contrato marco es el indicador de desempeño del
// contratista a lo largo de la vida del contrato.
//
// Regla: contratos sin RDs evaluados aún → media = null (UI muestra "Sin
// evaluar" o 0 ⭐). En cuanto el INT califica el primer RD, la media empieza
// a alimentarse.

export interface CalificacionMedia {
  /** Media aritmética de las calificaciones (1-5). `null` si sin evaluaciones. */
  media: number | null;
  /** Número de RDs con calificación contabilizados. */
  nEvaluaciones: number;
}

/**
 * Calcula la calificación media de un Contrato Marco a partir de los RDs
 * calificados de sus SOs.
 *
 * Considera RDs en estado APROBADO o EN_REVISION (sub-flag PL) — son los que
 * ya recibieron la calificación del INT. RDs en BORRADOR o EN_REVISION/INT
 * no se cuentan porque el INT todavía no propuso la nota.
 */
export function calificacionMediaContrato(
  contrato: Pick<ContratoMarco, "id">,
  sos: Pick<So, "id" | "contratoMarcoId">[],
  rds: Pick<Rd, "soId" | "estado" | "nivelRevision" | "calificacionInt">[],
): CalificacionMedia {
  const idsSos = new Set(
    sos.filter((s) => s.contratoMarcoId === contrato.id).map((s) => s.id),
  );
  if (idsSos.size === 0) return { media: null, nEvaluaciones: 0 };
  const calificaciones = rds
    .filter((r) => idsSos.has(r.soId))
    .filter((r) => typeof r.calificacionInt === "number")
    .filter(
      (r) =>
        r.estado === "APROBADO" ||
        (r.estado === "EN_REVISION" && r.nivelRevision === "PL"),
    )
    .map((r) => r.calificacionInt as number);
  if (calificaciones.length === 0) return { media: null, nEvaluaciones: 0 };
  const suma = calificaciones.reduce((a, b) => a + b, 0);
  return {
    media: suma / calificaciones.length,
    nEvaluaciones: calificaciones.length,
  };
}

/**
 * Calcula la media de calificación de todos los contratos en una sola pasada.
 * Devuelve Map<contratoId, CalificacionMedia>.
 */
export function calificacionMediaPorContrato(
  contratos: Pick<ContratoMarco, "id">[],
  sos: Pick<So, "id" | "contratoMarcoId">[],
  rds: Pick<Rd, "soId" | "estado" | "nivelRevision" | "calificacionInt">[],
): Map<string, CalificacionMedia> {
  const result = new Map<string, CalificacionMedia>();
  for (const c of contratos) {
    result.set(c.id, calificacionMediaContrato(c, sos, rds));
  }
  return result;
}
